import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import AdressInput from '../../Atoms/AdressInput';

const POSForm = ({ initialData, isEditing, onSuccess, onError }) => {
    const [formData, setFormData] = useState({
        fiche_agence: '',
        titre_affiche: '',
        adresse: '',
        design_points_of_sales: '',
        photos_projet: '',
        longitude: '',
        latitude: ''
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                longitude: initialData.longitude ? String(initialData.longitude) : '',
                latitude: initialData.latitude ? String(initialData.latitude) : ''
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Get form data including address input values
            const formDataObj = new FormData(e.target);
            const addressData = {
                adresse: formDataObj.get('adresse'),
                longitude: formDataObj.get('longitude'),
                latitude: formDataObj.get('latitude')
            };

            // Validate required fields
            if (!formData.titre_affiche || !addressData.adresse) {
                throw new Error('Title and address are required');
            }

            // Parse coordinates
            const data = {
                ...formData,
                adresse: addressData.adresse,
                longitude: addressData.longitude ? parseFloat(addressData.longitude) : null,
                latitude: addressData.latitude ? parseFloat(addressData.latitude) : null
            };

            if (isEditing && initialData?.id) {
                // Update existing POS
                const { error } = await supabase
                    .from('points_of_sale')
                    .update(data)
                    .eq('id', initialData.id);

                if (error) throw error;
            } else {
                // Create new POS
                const { error } = await supabase
                    .from('points_of_sale')
                    .insert([data]);

                if (error) throw error;
            }

            onSuccess();
            
            // Reset form if not editing
            if (!isEditing) {
                setFormData({
                    fiche_agence: '',
                    titre_affiche: '',
                    adresse: '',
                    design_points_of_sales: '',
                    photos_projet: '',
                    longitude: '',
                    latitude: ''
                });
                // Reset the form to clear AddressInput as well
                document.querySelector('form').reset();
            }
        } catch (error) {
            onError(error);
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '8px 12px',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        fontSize: '14px'
    };

    const labelStyle = {
        display: 'block',
        marginBottom: '4px',
        fontWeight: '500',
        fontSize: '14px',
        color: '#333'
    };

    return (
        <form onSubmit={handleSubmit} style={{ maxWidth: '600px' }}>
            <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                    Title/Display Name *
                    <input
                        type="text"
                        name="titre_affiche"
                        value={formData.titre_affiche}
                        onChange={handleChange}
                        required
                        style={inputStyle}
                        placeholder="Store or showroom name"
                    />
                </label>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                    Agency Reference
                    <input
                        type="text"
                        name="fiche_agence"
                        value={formData.fiche_agence}
                        onChange={handleChange}
                        style={inputStyle}
                        placeholder="Agency reference"
                    />
                </label>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                    Address *
                </label>
                <AdressInput
                    adressInputName="adresse"
                    latitutdeInputName="latitude"
                    longitudeInputName="longitude"
                    placeholder="Enter full address"
                    required={true}
                    adressDefaultValue={formData.adresse}
                    latitudeDefaultValue={formData.latitude}
                    longitudeDefaultValue={formData.longitude}
                />
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                    Design Points of Sales
                    <input
                        type="text"
                        name="design_points_of_sales"
                        value={formData.design_points_of_sales}
                        onChange={handleChange}
                        style={inputStyle}
                        placeholder="Category or type"
                    />
                </label>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                    Photo URLs
                    <input
                        type="text"
                        name="photos_projet"
                        value={formData.photos_projet}
                        onChange={handleChange}
                        style={inputStyle}
                        placeholder="Photo URLs (comma separated)"
                    />
                </label>
                
                {/* Image preview */}
                {formData.photos_projet && (
                    <div style={{ 
                        marginTop: '10px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: '10px'
                    }}>
                        {formData.photos_projet.split(',').map((url, index) => {
                            const trimmedUrl = url.trim();
                            if (!trimmedUrl) return null;
                            
                            return (
                                <div key={index} style={{
                                    position: 'relative',
                                    paddingBottom: '100%',
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '4px',
                                    overflow: 'hidden'
                                }}>
                                    <img
                                        src={trimmedUrl}
                                        alt={`Photo ${index + 1}`}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain'
                                        }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentElement.innerHTML = `
                                                <div style="
                                                    position: absolute;
                                                    top: 50%;
                                                    left: 50%;
                                                    transform: translate(-50%, -50%);
                                                    text-align: center;
                                                    color: #999;
                                                    font-size: 12px;
                                                    padding: 10px;
                                                ">
                                                    Invalid image URL
                                                </div>
                                            `;
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <button
                type="submit"
                disabled={loading}
                style={{
                    padding: '10px 20px',
                    backgroundColor: loading ? '#ccc' : '#0039d6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                    width: '100%'
                }}
            >
                {loading ? 'Saving...' : (isEditing ? 'Update Point of Sale' : 'Add Point of Sale')}
            </button>
        </form>
    );
};

export default POSForm;